import { Box3, type Bone, type Object3D, type Scene, type SkinnedMesh, Vector3 } from 'three'
import { MixamoMapper } from '../../retarget/bone-automap/MixamoMapper'
import { QuaterniusMapper } from '../../retarget/bone-automap/QuaterniusMapper'
import { TripoMapper } from '../../retarget/bone-automap/TripoMapper'
import { escape_html } from '../../lib/html.ts'

export type RigQualitySeverity = 'info' | 'warning' | 'error'

export interface RigQualityIssue {
  severity: RigQualitySeverity
  message: string
}

export interface RigQualitySummary {
  score: number
  skinned_mesh_count: number
  bone_count: number
  root_bone_count: number
  unweighted_vertex_count: number
  overweight_vertex_count: number
  largest_dimension: number
  detected_convention: string
  issues: RigQualityIssue[]
}

export class RigQualityReport {
  static analyze_scene (scene: Scene): RigQualitySummary {
    const skinned_meshes = this.collect_skinned_meshes(scene)
    const bones = this.collect_unique_bones(skinned_meshes)
    const root_bone_count = bones.filter((bone) => bone.parent === null || bone.parent.type !== 'Bone').length
    const bone_names = bones.map((bone) => bone.name)
    const weight_stats = this.calculate_weight_stats(skinned_meshes)
    const largest_dimension = this.calculate_largest_dimension(scene)
    const detected_convention = this.detect_convention(bone_names)
    const issues = this.create_issues(skinned_meshes.length, bones.length, root_bone_count, weight_stats.unweighted_vertex_count, weight_stats.overweight_vertex_count, largest_dimension, detected_convention)
    const score = this.calculate_score(issues)

    return {
      score,
      skinned_mesh_count: skinned_meshes.length,
      bone_count: bones.length,
      root_bone_count,
      unweighted_vertex_count: weight_stats.unweighted_vertex_count,
      overweight_vertex_count: weight_stats.overweight_vertex_count,
      largest_dimension,
      detected_convention,
      issues
    }
  }

  static to_html (summary: RigQualitySummary): string {
    const status = summary.issues.some(issue => issue.severity === 'error')
      ? 'Needs attention'
      : summary.issues.some(issue => issue.severity === 'warning')
        ? 'Usable with warnings'
        : 'Ready'

    const issue_items = summary.issues.length > 0
      ? summary.issues.map(issue => `<li><strong>${escape_html(issue.severity)}:</strong> ${escape_html(issue.message)}</li>`).join('')
      : '<li>No rig readiness issues detected.</li>'

    return `
      <div class="rig-quality-report">
        <p><strong>Status:</strong> ${escape_html(status)}</p>
        <p><strong>Score:</strong> ${summary.score}/100</p>
        <ul>
          <li><strong>Detected rig:</strong> ${escape_html(summary.detected_convention)}</li>
          <li><strong>Skinned meshes:</strong> ${summary.skinned_mesh_count}</li>
          <li><strong>Bones:</strong> ${summary.bone_count}</li>
          <li><strong>Root bones:</strong> ${summary.root_bone_count}</li>
          <li><strong>Unweighted vertices:</strong> ${summary.unweighted_vertex_count}</li>
          <li><strong>Overweight vertices:</strong> ${summary.overweight_vertex_count}</li>
          <li><strong>Largest dimension:</strong> ${summary.largest_dimension.toFixed(2)} units</li>
        </ul>
        <h3>Checks</h3>
        <ul>${issue_items}</ul>
      </div>
    `
  }

  private static collect_skinned_meshes (scene: Scene): SkinnedMesh[] {
    const skinned_meshes: SkinnedMesh[] = []
    scene.traverse((child: Object3D) => {
      if (child.type === 'SkinnedMesh') {
        skinned_meshes.push(child as SkinnedMesh)
      }
    })
    return skinned_meshes
  }

  private static collect_unique_bones (skinned_meshes: SkinnedMesh[]): Bone[] {
    const bones_by_uuid = new Map<string, Bone>()
    skinned_meshes.forEach((mesh) => {
      mesh.skeleton.bones.forEach((bone) => {
        bones_by_uuid.set(bone.uuid, bone)
      })
    })
    return Array.from(bones_by_uuid.values())
  }

  private static calculate_largest_dimension (scene: Scene): number {
    const bounding_box = new Box3().setFromObject(scene)
    const size = new Vector3()
    bounding_box.getSize(size)
    return Math.max(size.x, size.y, size.z)
  }

  private static calculate_weight_stats (skinned_meshes: SkinnedMesh[]): { unweighted_vertex_count: number, overweight_vertex_count: number } {
    let unweighted_vertex_count = 0
    let overweight_vertex_count = 0
    for (const mesh of skinned_meshes) {
      const weights = mesh.geometry.getAttribute('skinWeight')
      const positions = mesh.geometry.getAttribute('position')
      if (weights === undefined || positions === undefined) continue
      for (let i = 0; i < positions.count; i++) {
        let sum = 0
        for (let j = 0; j < weights.itemSize; j++) {
          sum += weights.getComponent(i, j)
        }
        if (sum < 0.001) unweighted_vertex_count++
        else if (sum > 1.01) overweight_vertex_count++
      }
    }
    return { unweighted_vertex_count, overweight_vertex_count }
  }

  private static detect_convention (bone_names: string[]): string {
    if (bone_names.length === 0) return 'No skeleton detected'
    if (MixamoMapper.is_target_valid_skeleton(bone_names)) return 'Mixamo'
    if (TripoMapper.is_target_valid_skeleton(bone_names)) return 'Tripo3D'
    if (QuaterniusMapper.is_target_valid_skeleton(bone_names)) return 'Universal humanoid'
    return 'Custom / unknown'
  }

  private static create_issues (
    skinned_mesh_count: number,
    bone_count: number,
    root_bone_count: number,
    unweighted_vertex_count: number,
    overweight_vertex_count: number,
    largest_dimension: number,
    detected_convention: string
  ): RigQualityIssue[] {
    const issues: RigQualityIssue[] = []

    if (skinned_mesh_count === 0) {
      issues.push({ severity: 'error', message: 'No SkinnedMesh objects were found.' })
    }

    if (bone_count === 0) {
      issues.push({ severity: 'error', message: 'No skeleton bones were found.' })
    }

    if (root_bone_count > 1) {
      issues.push({ severity: 'warning', message: 'Multiple root bones were found; retargeting may need manual cleanup.' })
    }

    if (unweighted_vertex_count > 0) {
      issues.push({ severity: 'error', message: `${unweighted_vertex_count} vertices have no skin weight; run or repair skinning before export.` })
    }

    if (overweight_vertex_count > 0) {
      issues.push({ severity: 'warning', message: `${overweight_vertex_count} vertices have weights summing above 1.0; normalize weights before export.` })
    }

    if (largest_dimension > 20) {
      issues.push({ severity: 'warning', message: 'The model is very large and will be scaled down for preview.' })
    } else if (largest_dimension > 0 && largest_dimension < 0.05) {
      issues.push({ severity: 'warning', message: 'The model is extremely small; check unit scale before export.' })
    }

    if (detected_convention === 'Custom / unknown') {
      issues.push({ severity: 'info', message: 'No known vendor naming convention was detected, so manual mapping may be needed.' })
    }

    return issues
  }

  private static calculate_score (issues: RigQualityIssue[]): number {
    const penalty = issues.reduce((sum, issue) => {
      if (issue.severity === 'error') return sum + 30
      if (issue.severity === 'warning') return sum + 12
      return sum + 4
    }, 0)
    return Math.max(0, Math.min(100, 100 - penalty))
  }
}

